import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Status from "./pages/Status";
import AuthCallback from "./pages/AuthCallback";
import Booking from "./pages/Booking";
import Payment from "./pages/Payment";
// Code-split heavy public pages so /artists, /schedule, /competition chunks
// (with their assets and modals) only download when the route is clicked.
const Artists = lazy(() => import("./pages/Artists"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Competition = lazy(() => import("./pages/Competition"));
const MapSchedule = lazy(() => import("./pages/MapSchedule"));
const FestivalInfo = lazy(() => import("./pages/FestivalInfo"));
const Livestream = lazy(() => import("./pages/Livestream"));
const FestivalInformation = lazy(() => import("./pages/festival-info/FestivalInformation"));
const MapSchedulePage = lazy(() => import("./pages/festival-info/MapSchedulePage"));
const ParkingRouteMap = lazy(() => import("./pages/festival-info/ParkingRouteMap"));
const LibreSakay = lazy(() => import("./pages/festival-info/LibreSakay"));
const FaqPage = lazy(() => import("./pages/festival-info/FaqPage"));
const GettingThere = lazy(() => import("./pages/festival-info/GettingThere"));
const Experience = lazy(() => import("./pages/Experience"));
const HotBuysHotFoods = lazy(() => import("./pages/experience/HotBuysHotFoods"));
const ExpAccommodations = lazy(() => import("./pages/experience/Accommodations"));
const ExpTransport = lazy(() => import("./pages/experience/Transport"));

import CamperShell from "./components/layout/CamperShell";
import CamperHome from "./pages/camper/CamperHome";
import CamperQR from "./pages/camper/CamperQR";
import CamperSchedule from "./pages/camper/CamperSchedule";
import CamperAnnouncements from "./pages/camper/CamperAnnouncements";
import CamperFeedback from "./pages/camper/CamperFeedback";

import AdminShell from "./components/layout/AdminShell";
import AdminOperations from "./pages/admin/AdminOperations";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminRegistry from "./pages/admin/AdminRegistry";
import AdminScanner from "./pages/admin/AdminScanner";
import AdminActivities from "./pages/admin/AdminActivities";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import AdminCMS from "./pages/admin/AdminCMS";
import AdminSafety from "./pages/admin/AdminSafety";
import AdminFeedback from "./pages/admin/AdminFeedback";
import AdminVVIPTracker from "./pages/admin/AdminVVIPTracker";
import AdminVVIPCodes from "./pages/admin/AdminVVIPCodes";
import AdminArtists from "./pages/admin/AdminArtists";
import AdminCompetitions from "./pages/admin/AdminCompetitions";
import AdminParkingLibreSakay from "./pages/admin/AdminParkingLibreSakay";
import AdminFAQs from "./pages/admin/AdminFAQs";
import AdminExperience from "./pages/admin/AdminExperience";

import { MapScheduleFab } from "./components/common/MapScheduleFab";

const PrivateRoute = ({ role }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    // staff_vvip and staff_camp can see admin views
    if (role === "admin" && ["staff_vvip", "staff_camp"].includes(user.role)) return <Outlet />;
    return <Navigate to={user.role === "admin" ? "/admin" : "/camper"} replace />;
  }
  return <Outlet />;
};

function AppRouter() {
  const location = useLocation();
  // Process Google OAuth callback FIRST (before routes / protected route checks)
  if (location.pathname === "/auth/callback") return <AuthCallback />;
  // Show FAB only on public-facing pages
  const showFab = ["/", "/artists", "/status", "/login", "/schedule", "/competition", "/map-schedule", "/festival-info", "/experience", "/livestream"].some((p) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p)
  );
  return (
    <>
    <Suspense fallback={<div className="min-h-screen grid place-items-center text-[#3D1E6D] text-sm font-bold uppercase tracking-widest" data-testid="route-suspense">Loading…</div>}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/status" element={<Status />} />
      <Route path="/artists" element={<Artists />} />
      <Route path="/schedule" element={<Schedule />} />
      <Route path="/competition" element={<Competition />} />
      <Route path="/livestream" element={<Livestream />} />
      <Route path="/map-schedule" element={<MapSchedule />} />
      <Route path="/festival-info" element={<FestivalInfo />}>
        <Route index element={<FestivalInformation />} />
        <Route path="map-schedule" element={<MapSchedulePage />} />
        <Route path="parking" element={<ParkingRouteMap />} />
        <Route path="libre-sakay" element={<LibreSakay />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="getting-there" element={<GettingThere />} />
      </Route>
      <Route path="/experience" element={<Experience />}>
        <Route index element={<HotBuysHotFoods />} />
        <Route path="accommodations" element={<ExpAccommodations />} />
        <Route path="transport" element={<ExpTransport />} />
      </Route>

      <Route element={<PrivateRoute role="camper" />}>
        <Route path="/booking" element={<Booking />} />
        <Route path="/payment/:id" element={<Payment />} />
        <Route path="/camper" element={<CamperShell />}>
          <Route index element={<CamperHome />} />
          <Route path="qr" element={<CamperQR />} />
          <Route path="schedule" element={<CamperSchedule />} />
          <Route path="announcements" element={<CamperAnnouncements />} />
          <Route path="feedback" element={<CamperFeedback />} />
        </Route>
      </Route>

      <Route element={<PrivateRoute role="admin" />}>
        <Route path="/admin" element={<AdminShell />}>
          <Route index element={<AdminOperations />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="registry" element={<AdminRegistry />} />
          <Route path="vvip-tracker" element={<AdminVVIPTracker />} />
          <Route path="vvip-codes" element={<AdminVVIPCodes />} />
          <Route path="artists" element={<AdminArtists />} />
          <Route path="competitions" element={<AdminCompetitions />} />
          <Route path="scanner" element={<AdminScanner />} />
          <Route path="activities" element={<AdminActivities />} />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="cms" element={<AdminCMS />} />
          <Route path="experience" element={<AdminExperience />} />
          <Route path="parking-libresakay" element={<AdminParkingLibreSakay />} />
          <Route path="safety" element={<AdminSafety />} />
          <Route path="faqs" element={<AdminFAQs />} />
          <Route path="feedback" element={<AdminFeedback />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    {showFab && <MapScheduleFab />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                border: "2px solid #3D1E6D",
                borderRadius: 0,
                boxShadow: "4px 4px 0 #3D1E6D",
                fontWeight: 700,
              },
            }}
          />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
